import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error('Please accept the User Agreement and Privacy Policy');
      return;
    }
    setLoading(true);

    try {
      await login(email, password); 
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const backgroundImageUrl = `${process.env.PUBLIC_URL}/images/login-bg.png`;

  return (
    <div style={styles.wrapper}>
      {/* Background image */}
      <div
        style={{
          ...styles.bgImage,
          backgroundImage: `url(${backgroundImageUrl})`,
        }}
      />
      {/* Opacity overlay */}
      <div style={styles.overlay} />

      {/* Login card */}
      <div style={styles.cardOuter}>
        <div style={styles.card}>
          {/* WTI Logo + Branding */}
          <div style={styles.logoSection}>
            <div style={styles.brandRow}>
              <span style={styles.brandLetter}>w</span>
              <span style={styles.brandLetter}>t</span>
              <span style={styles.brandLetterI}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/logo.png`}
                  alt="WTI"
                  style={styles.logoPin}
                />
                <span style={styles.dotlessI}>ı</span>
              </span>
            </div>
            <div style={styles.brandSubtext}>where to invest</div>
          </div>

          <p style={styles.heading}>Log in to continue to Where To Invest</p>

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Email */}
            <div style={styles.inputGroup}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address*"
                required
                style={styles.input}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#1a73e8';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(26,115,232,0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#dadce0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Password */}
            <div style={styles.inputGroup}>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password*"
                  required
                  style={{ ...styles.input, paddingRight: '48px' }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1a73e8';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(26,115,232,0.15)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dadce0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.eyeButton}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    // Eye-off icon
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    // Eye icon
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxLabel}>
                I accept the{' '}
                <Link to="/user-agreement" target="_blank" rel="noopener noreferrer" style={styles.checkboxLink}>
                  User Agreement
                </Link>
                {' '}and{' '}
                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={styles.checkboxLink}>
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* Forgot password */}
            <div style={styles.forgotRow}>
              <Link to="/forgot-password" style={styles.forgotLink}>
                Forgot password?
              </Link>
            </div>

            {/* Continue button */}
            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              style={{
                ...styles.continueBtn,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Logging in...' : 'Continue'}
            </button>
          </form>

          {/* Payment-only access */}
          <p style={styles.registerRow}>
            New to WTI?{' '}
            <a
              href="https://wheretoinvest.io"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.registerLink}
            >
              Purchase access
            </a>
            {' '}to receive your login email.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    minHeight: '100vh',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bgImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 1,
  },
  cardOuter: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '440px',
    padding: '24px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #1a73e8',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    padding: '48px 40px 36px',
  },
  logoSection: {
    textAlign: 'center' as const,
    marginBottom: '24px',
  },
  brandRow: {
    display: 'inline-flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    lineHeight: 1,
  },
  brandLetter: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#222',
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    lineHeight: 1,
  },
  brandLetterI: {
    fontSize: '48px',
    fontWeight: 900,
    color: '#222',
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    position: 'relative' as const,
    lineHeight: 1,
    display: 'inline-block',
  },
  dotlessI: {
    fontSize: '48px',
    fontWeight: 900,
    fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif",
    lineHeight: 1,
  },
  logoPin: {
    position: 'absolute' as const,
    top: '-6px',
    left: '50%',
    transform: 'translateX(-50%)',
    height: '28px',
    width: 'auto',
  },
  brandSubtext: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1a73e8',
    letterSpacing: '0.5px',
    marginTop: '4px',
  },
  heading: {
    textAlign: 'center' as const,
    fontSize: '16px',
    color: '#3c4043',
    fontWeight: 400,
    marginBottom: '32px',
    lineHeight: '1.5',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0px',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: '#fff',
    color: '#202124',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box' as const,
  },
  passwordWrapper: {
    position: 'relative' as const,
  },
  eyeButton: {
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  forgotRow: {
    marginBottom: '28px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '16px',
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: '3px',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#3c4043',
    lineHeight: 1.45,
  },
  checkboxLink: {
    color: '#1a73e8',
    fontWeight: 600,
    textDecoration: 'underline',
  },
  forgotLink: {
    color: '#1a73e8',
    fontSize: '14px',
    fontWeight: 600,
    textDecoration: 'none',
  },
  continueBtn: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#1a73e8',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    letterSpacing: '0.3px',
  },
  registerRow: {
    marginTop: '24px',
    textAlign: 'center' as const,
    fontSize: '14px',
    color: '#5f6368',
  },
  registerLink: {
    color: '#1a73e8',
    fontWeight: 600,
    textDecoration: 'none',
  },
};

export default Login;
